import { Icon } from '@livable/saas-react-components';
import { Button, LinearProgress, Typography } from '@mui/material';
import React from 'react';
import { AttachPropertyPicker } from './AttachPropertyPicker';
import useStyles from './attachBillModalStyles';
const formatUsd = amount => typeof amount === 'number' && !Number.isNaN(amount) ? new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD'
}).format(amount) : null;
const capitalise = text => text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
const describeSection = section => {
  const services = section.services.map(capitalise).join(', ');
  const account = section.accountNumber ? "Account ".concat(section.accountNumber) : 'No account number printed';
  return services ? "".concat(services, " \xB7 ").concat(account) : account;
};

/**
 * The sections of a portfolio bill (LB-5910), one card each. A section validation matched is shown as
 * settled — the property it was matched to, nothing to do. A section it could not match asks one
 * question, "which property is this for?", and once answered offers to use the same property for the
 * other sections still open, since a statement's leftovers usually belong to one property.
 */
export const AttachBillSections = _ref => {
  let sections = _ref.sections,
    choices = _ref.choices,
    propertyNameOf = _ref.propertyNameOf,
    properties = _ref.properties,
    disabled = _ref.disabled,
    onChoose = _ref.onChoose,
    onUseForRest = _ref.onUseForRest;
  const classes = useStyles();
  const open = sections.filter(section => !section.matchedPropertyId);
  const settled = sections.length - open.filter(section => !choices[section.blockIndex]).length;
  return /*#__PURE__*/React.createElement("div", {
    className: classes.sections
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.progress
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.progressText
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    className: classes.progressCount
  }, settled, " of ", sections.length, " sections have a property"), settled < sections.length && /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "light"
  }, sections.length - settled, " to choose")), /*#__PURE__*/React.createElement(LinearProgress, {
    variant: "determinate",
    value: settled / sections.length * 100,
    className: classes.progressBar,
    "aria-label": "Sections with a property"
  })), sections.map(section => {
    const matched = !!section.matchedPropertyId;
    const chosen = choices[section.blockIndex] || '';
    const stillOpenElsewhere = open.filter(other => other.blockIndex !== section.blockIndex && !choices[other.blockIndex]).length;
    // Numbered by its place on the bill, as the service's notes name it.
    const heading = section.address || "Section ".concat(section.blockIndex + 1);
    const headingId = "attach-section-".concat(section.blockIndex, "-heading");
    const amount = formatUsd(section.amount);
    return /*#__PURE__*/React.createElement("div", {
      key: section.blockIndex,
      className: "".concat(classes.section, " ").concat(matched ? classes.sectionMatched : ''),
      "data-testid": "attach-section-".concat(section.blockIndex)
    }, /*#__PURE__*/React.createElement("div", {
      className: classes.sectionHeader
    }, /*#__PURE__*/React.createElement("span", {
      className: classes.sectionStatus
    }, /*#__PURE__*/React.createElement(Icon, {
      type: matched || chosen ? 'success' : 'warning',
      state: matched || chosen ? 'success' : 'warning'
    }), /*#__PURE__*/React.createElement("span", {
      className: classes.visuallyHidden
    }, matched ? 'Matched' : chosen ? 'Property chosen' : 'Needs a property')), /*#__PURE__*/React.createElement("div", {
      className: classes.sectionTitle
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "body1",
      className: classes.sectionAddress,
      id: headingId
    }, heading), /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      color: "light"
    }, describeSection(section))), amount && /*#__PURE__*/React.createElement(Typography, {
      variant: "body1",
      className: classes.sectionAmount
    }, amount)), matched ? /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      className: classes.sectionMatchedTo
    }, /*#__PURE__*/React.createElement(Icon, {
      type: "building",
      state: "grey"
    }), "Matched to ", propertyNameOf(section.matchedPropertyId)) : /*#__PURE__*/React.createElement("div", {
      className: classes.sectionChoice
    }, /*#__PURE__*/React.createElement(AttachPropertyPicker, {
      id: "attach-section-".concat(section.blockIndex, "-property"),
      properties: properties,
      value: chosen,
      onChange: propertyId => onChoose(section.blockIndex, propertyId),
      label: "Which property is this for?",
      placeholder: "Search by address or name",
      disabled: disabled,
      describedBy: headingId
    }), chosen && stillOpenElsewhere > 0 && /*#__PURE__*/React.createElement(Button, {
      variant: "text",
      size: "small",
      className: classes.useForRest,
      onClick: () => onUseForRest(chosen),
      disabled: disabled
    }, "Use this property for the", ' ', stillOpenElsewhere === 1 ? 'other section' : "".concat(stillOpenElsewhere, " other sections"), " still open")));
  }));
};